import { Suspense, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Card, CardTitle, cn, useToast } from "@/core/ui";
import { ApiError } from "@/core/api/client";
import { useTenant } from "@/core/tenant/TenantProvider";
import { usePuzzle } from "@/core/puzzles/api";
import { DifficultyStars } from "@/core/puzzles/PuzzleListPage";
import { PuzzleCreditLine } from "@/core/puzzles/Credit";
import { RulesText, extractRules } from "@/core/puzzles/PuzzlePreviewPage";
import { useTeams, teamsKey, type Team } from "@/core/teams/api";
import { useQueryClient } from "@tanstack/react-query";
import { ChatPanel } from "@/core/chat/ChatPanel";
import { useRealtimeOpen, useTopic } from "@/core/realtime/RealtimeProvider";
import { attemptTopic, teamTopic } from "@/core/realtime/protocol";
import { getPuzzleType, type PuzzleTypeModule } from "@/puzzle-types/registry";
import type { Feedback, PlayerPresence } from "@/puzzle-types/types";
import {
  findAttempt,
  useAttempt,
  useExample,
  useLeaderboard,
  useMyAttempts,
  useReveal,
  useSaveProgress,
  useSubmit,
  type MyAttemptsEntry,
  type RevealPayload,
} from "./api";
import { SolveCelebration } from "./Celebration";
import { LeaderboardPanel } from "./LeaderboardPanel";
import { SheetLink } from "@/core/sheet/SheetLink";
import { PuzzleStatsPanel } from "./PuzzleStatsPanel";
import { shouldApplyRemote } from "./sync";
import { usePresence } from "./usePresence";
import { useSolverCount } from "./useSolvers";
import { Timer } from "./Timer";
import { formatDuration } from "./time";

/** One line per code, keeping the first item's interpolation values (the player's own word…). */
export function dedupeFeedback(
  items: Feedback[],
): { code: string; params: Record<string, unknown> }[] {
  const seen = new Map<string, Record<string, unknown>>();
  for (const item of items) {
    const code = item.type || item.code;
    if (!seen.has(code)) seen.set(code, item.params ?? {});
  }
  return Array.from(seen, ([code, params]) => ({ code, params }));
}

/**
 * The core "chrome" around any puzzle type: reveal, timer, submit, attempts, cooldown, rules,
 * leaderboard. The type only renders its Player (via the registry).
 *
 * Whom the page plays as — the user, or one of their teams — is carried by `?team=<id>`, so a
 * reload, a shared link and the switcher below all resume the same attempt (ADR 0025).
 */
export function PlayPage() {
  const { slug = "" } = useParams();
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const puzzle = usePuzzle(slug);
  const attempts = useMyAttempts();
  const teams = useTeams();
  const reveal = useReveal(slug);

  const myTeams = useMemo(
    () => teams.data?.results.filter((team) => team.is_member) ?? [],
    [teams.data],
  );
  const requested = Number(params.get("team")) || null;
  // A team the user is no longer part of (a stale link) falls back to the personal attempt.
  const teamId =
    requested !== null && myTeams.some((team) => team.id === requested) ? requested : null;
  const entry = attempts.data?.[slug];
  const existing = findAttempt(entry, teamId);
  // A reveal only counts for the participant it was made for: switching `?team=` right after
  // one must not keep showing the other attempt while the attempt list refetches.
  const revealed =
    reveal.data && (reveal.data.attempt.team?.id ?? null) === teamId ? reveal.data : null;
  // The run just started wins over the list: a replay makes `existing` stale for a moment,
  // and the solved board must not flash back before the refetch lands (ADR 0037).
  const attemptId = revealed?.attempt.id ?? existing?.id ?? null;
  const live = useRealtimeOpen();
  const attempt = useAttempt(attemptId, { live });
  const payload = attempt.data ?? revealed ?? null;
  // Before the reveal this page only watches: `ActivePlay` is what counts itself in.
  const solvers = useSolverCount(slug, false);
  // Fetched before the reveal on purpose: the example is not this puzzle (ADR 0069), and a
  // type without one simply offers nothing.
  const example = useExample(payload ? undefined : puzzle.data?.puzzle_type, i18n.language);

  // A ranked run needs every player in it to come in blind (ADR 0068). The page sees the
  // user's own runs; a teammate's surfaces as the server's refusal, for this participant only.
  const rankedClosed =
    (entry !== undefined && (entry.personal !== null || entry.teams.length > 0)) ||
    (reveal.error instanceof ApiError &&
      reveal.error.code === "played_elsewhere" &&
      (reveal.variables?.team ?? null) === teamId);

  const selectParticipant = (id: number | null) =>
    setParams(id ? { team: String(id) } : {}, { replace: true });

  if (puzzle.isPending || attempts.isPending || teams.isPending)
    return <p className="bt-page-message">{t("app.loading")}</p>;
  if (puzzle.isError || !puzzle.data) return <Alert tone="danger">{t("app.error_generic")}</Alert>;

  if (!payload) {
    return (
      <section className="bt-play">
        <Card className="bt-card--sheet">
          <h1 className="bt-play__teaser-title">{puzzle.data.title}</h1>
          <p className="bt-play__author">{t("puzzles.by", { author: puzzle.data.author })}</p>
          <div className="bt-play__teaser-meta">
            <DifficultyStars value={puzzle.data.difficulty} />
            <Badge tone="info">
              {t(`puzzle_types.${puzzle.data.puzzle_type}`, {
                defaultValue: puzzle.data.puzzle_type,
              })}
            </Badge>
          </div>
          <SolverCount count={solvers} />
          <ParticipantSwitcher entry={entry} teamId={teamId} onSelect={selectParticipant} />
          <Alert tone="info">{t("play.reveal_hint")}</Alert>
          {myTeams.length > 0 ? (
            <ParticipantPicker teams={myTeams} teamId={teamId} onSelect={selectParticipant} />
          ) : null}
          {rankedClosed ? (
            <div className="bt-play__reveal-hint">
              <Alert tone="warning">
                {t(teamId === null ? "play.ranked_closed_solo" : "play.ranked_closed_team")}
              </Alert>
            </div>
          ) : null}
          <div className="bt-play__reveal-actions">
            {rankedClosed ? null : (
              <Button
                size="lg"
                loading={reveal.isPending}
                onClick={() => reveal.mutate({ team: teamId })}
              >
                {t("play.reveal")}
              </Button>
            )}
            <Button
              size="lg"
              variant="outline"
              loading={reveal.isPending}
              onClick={() => reveal.mutate({ ranked: false, team: teamId })}
            >
              {t("play.reveal_training")}
            </Button>
          </div>
          {rankedClosed ? null : (
            <div className="bt-play__reveal-hint">
              <Alert tone="warning">{t("play.reveal_training_hint")}</Alert>
            </div>
          )}
          {example.data ? (
            <p className="bt-example__invite">
              <span>
                {t("example.invite", {
                  type: t(`puzzle_types.${puzzle.data.puzzle_type}`, {
                    defaultValue: example.data.label,
                  }),
                })}
              </span>
              <Link
                to={`/examples/${encodeURIComponent(puzzle.data.puzzle_type)}?from=${encodeURIComponent(slug)}`}
                className="bt-button bt-button--outline bt-button--md"
              >
                {t("example.try")}
              </Link>
            </p>
          ) : null}
        </Card>
        <div className="bt-play__column">
          {teamId !== null ? <ChatPanel teamId={teamId} /> : null}
          <LeaderboardPanel slug={slug} />
          <PuzzleStatsPanel slug={slug} />
        </div>
      </section>
    );
  }
  return (
    <ActivePlay
      key={payload.attempt.id}
      payload={payload}
      slug={slug}
      entry={entry}
      onSelectParticipant={selectParticipant}
      onReplay={() => reveal.mutate({ replay: true, team: teamId })}
      replaying={reveal.isPending}
    />
  );
}

/**
 * How many other people are on this puzzle right now (ADR 0038). A number and no name, so it
 * is as safe before the reveal as after; silent when nobody else is there, or when the socket
 * that feeds it is down.
 */
function SolverCount({ count }: { count: number | null }) {
  const { t } = useTranslation();
  if (count === null || count === 0) return null;
  return (
    <p className="bt-play__solvers" role="status" aria-live="polite">
      {t("play.solvers", { count })}
    </p>
  );
}

/** "Play as": the user, or one of their teams. Checked state is styled from ARIA (§12). */
function ParticipantPicker({
  teams,
  teamId,
  onSelect,
}: {
  teams: Team[];
  teamId: number | null;
  onSelect: (id: number | null) => void;
}) {
  const { t } = useTranslation();
  const labelId = useId();
  return (
    <div className="bt-play__participant" role="radiogroup" aria-labelledby={labelId}>
      <span id={labelId} className="bt-play__participant-label">
        {t("play.play_as")}
      </span>
      <div className="bt-play__participant-options">
        <button
          type="button"
          role="radio"
          aria-checked={teamId === null}
          className="bt-play__participant-option"
          onClick={() => onSelect(null)}
        >
          {t("play.play_as_me")}
        </button>
        {teams.map((team) => (
          <button
            key={team.id}
            type="button"
            role="radio"
            aria-checked={teamId === team.id}
            className="bt-play__participant-option"
            onClick={() => onSelect(team.id)}
          >
            {team.name}
          </button>
        ))}
      </div>
      {teamId !== null ? (
        <p className="bt-play__participant-hint">{t("play.play_as_team_hint")}</p>
      ) : null}
    </div>
  );
}

/** Links to the same puzzle as another participant who already has an attempt. */
function ParticipantSwitcher({
  entry,
  teamId,
  onSelect,
}: {
  entry: MyAttemptsEntry | undefined;
  teamId: number | null;
  onSelect: (id: number | null) => void;
}) {
  const { t } = useTranslation();
  const others = [
    ...(teamId !== null && entry?.personal
      ? [{ id: null, label: t("play.switch_personal"), status: entry.personal.status }]
      : []),
    ...(entry?.teams ?? [])
      .filter((attempt) => attempt.team && attempt.team.id !== teamId)
      .map((attempt) => ({
        id: attempt.team?.id ?? null,
        label: t("play.switch_team", { name: attempt.team?.name }),
        status: attempt.status,
      })),
  ];
  if (others.length === 0) return null;
  return (
    <nav className="bt-play__switcher" aria-label={t("play.switch_title")}>
      {others.map((other) => (
        <Button key={other.id ?? "me"} variant="ghost" size="sm" onClick={() => onSelect(other.id)}>
          {other.label}{" "}
          <span className="bt-play__switcher-status">
            ({t(`play.attempt_status.${other.status}`)})
          </span>
        </Button>
      ))}
    </nav>
  );
}

function progressFromPayload(module: PuzzleTypeModule, payload: RevealPayload): unknown {
  if (module.migrateProgress)
    return module.migrateProgress(payload.definition, payload.progress_state);
  return payload.progress_state ?? module.emptyProgress(payload.definition);
}

function ActivePlay({
  payload,
  slug,
  entry,
  onSelectParticipant,
  onReplay,
  replaying,
}: {
  payload: RevealPayload;
  slug: string;
  entry: MyAttemptsEntry | undefined;
  onSelectParticipant: (id: number | null) => void;
  onReplay: () => void;
  replaying: boolean;
}) {
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  const { toast } = useToast();
  const qc = useQueryClient();
  const live = useRealtimeOpen();
  const receivedAt = useMemo(() => Date.now(), []);
  // The clock offset pairs `server_now` with `receivedAt`; a polled payload carries a fresh
  // `server_now`, so the one read at mount is the one that stays paired.
  const [serverNow] = useState(() => payload.attempt.server_now);
  const module = getPuzzleType(payload.puzzle.puzzle_type);
  const save = useSaveProgress(payload.attempt.id);
  const submit = useSubmit(payload.attempt.id);
  const [progress, setProgress] = useState<unknown>(() =>
    module ? progressFromPayload(module, payload) : null,
  );
  // The newest progress, whoever set it: what a submission serialises. A Player that edits
  // and asks to submit in the same tick must not have last render's state sent (ADR 0041).
  const progressRef = useRef<unknown>(progress);
  const [cooldownUntil, setCooldownUntil] = useState<number>(
    () => Date.now() + payload.attempt.cooldown_remaining * 1000,
  );
  const [now, setNow] = useState(() => Date.now());
  const [lastFeedback, setLastFeedback] = useState<Feedback[]>([]);
  const saveTimer = useRef<number | null>(null);
  // What the confetti is thrown from, and what punches when the clock stops.
  const timerRef = useRef<HTMLDivElement>(null);
  // This page instance watched the solve happen. Reopening a solved board is a quiet page.
  const [celebrated, setCelebrated] = useState(false);
  // Newest progress stamp this client saved or applied: the bar a polled state must clear.
  const lastStampRef = useRef<string | null>(payload.attempt.progress_updated_at);
  const wasSolvedRef = useRef(payload.attempt.status === "solved");
  const team = payload.attempt.team;
  const isTeam = team !== null;
  const solved = payload.attempt.status === "solved";
  const canAutosave = payload.capabilities.autosave;
  // A teammate's autosave or submission lands in the query cache; the fold effect below
  // then treats it exactly like a polled payload (ADR 0029).
  const attemptId = payload.attempt.id;
  useTopic(isTeam ? attemptTopic(attemptId) : null, (frame) => {
    if (frame.type === "attempt.changed") {
      qc.setQueryData<RevealPayload>(["attempts", attemptId], (old) =>
        old
          ? { ...old, attempt: frame.data.attempt, progress_state: frame.data.progress_state }
          : old,
      );
    } else if (frame.type === "attempt.restarted") {
      // A teammate opened a new run: the list now points at it, and the page follows.
      void qc.invalidateQueries({ queryKey: ["attempts", "mine"] });
    } else if (frame.type === "topic.revoked") {
      // No longer a member: the attempt list falls back to the personal attempt.
      void qc.invalidateQueries({ queryKey: teamsKey });
      void qc.invalidateQueries({ queryKey: ["attempts", "mine"] });
    }
  });
  useTopic(team ? teamTopic(team.id) : null, (frame) => {
    if (frame.type === "team.changed") void qc.invalidateQueries({ queryKey: teamsKey });
  });

  // Presence is a team affair and the type's to draw: without both, the Player gets nothing.
  const presenceEnabled = isTeam && !solved && payload.capabilities.presence;
  // An open board counts its player in; a solved one stops, which is what removes them.
  const solvers = useSolverCount(slug, !solved);
  const presence = usePresence(payload.attempt.id, presenceEnabled);
  const playerPresence = useMemo<PlayerPresence | undefined>(
    () =>
      presenceEnabled
        ? {
            peers: presence.peers.map((peer, index) => ({
              id: peer.user_id,
              name: peer.name,
              colorIndex: index,
              state: peer.state,
            })),
            report: presence.report,
          }
        : undefined,
    [presenceEnabled, presence.peers, presence.report],
  );

  // The placing, from the board the panel next door already polls — so it is there on a
  // reload too, not only in the second after submitting. A practice run has no row.
  const board = useLeaderboard(slug, { live });
  const rank =
    solved && payload.attempt.is_ranked
      ? (submit.data?.rank ??
        board.data?.entries.find((entry) => entry.is_me && entry.is_team === isTeam)?.rank ??
        null)
      : null;
  // Points only mean something for a type that declares them (ADR 0040).
  const score =
    solved && payload.capabilities.scored
      ? (submit.data?.score ?? payload.attempt.score ?? null)
      : null;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const persistProgress = useCallback(
    (next: unknown) => {
      saveTimer.current = null; // no longer pending: in flight, see `save.isPending`
      save.mutate(next, {
        // A refused save (the server held something newer) leaves the bar where it is, so
        // the next poll brings that newer state in.
        onSuccess: (result) => {
          if (result.stored && result.progress_updated_at)
            lastStampRef.current = result.progress_updated_at;
        },
      });
    },
    [save],
  );

  const onProgressChange = useCallback(
    (next: unknown) => {
      progressRef.current = next;
      setProgress(next);
      setLastFeedback([]);
      if (!canAutosave || solved) return;
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => persistProgress(next), 2000); // debounce 2 s
    },
    [canAutosave, solved, persistProgress],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    },
    [],
  );

  // Team attempts are polled (see `useAttempt`): fold what teammates did into the board.
  useEffect(() => {
    if (!isTeam || !module) return;
    const remote = payload.attempt;
    const becameSolved = remote.status === "solved" && !wasSolvedRef.current;
    if (becameSolved) {
      wasSolvedRef.current = true;
      // A teammate won: the board is theirs now, a pending local edit is moot.
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      toast(
        t(
          payload.capabilities.scored ? "play.team_solved_toast_scored" : "play.team_solved_toast",
          { time: formatDuration(remote.elapsed_ms ?? 0), points: remote.score ?? 0 },
        ),
        "success",
      );
      setCelebrated(true); // the team's win is the whole team's win
    }
    if (!canAutosave) return;
    const localPending = saveTimer.current !== null || save.isPending;
    if (
      !becameSolved &&
      !shouldApplyRemote(remote.progress_updated_at, lastStampRef.current, localPending)
    )
      return;
    lastStampRef.current = remote.progress_updated_at;
    const folded = progressFromPayload(module, payload);
    progressRef.current = folded;
    setProgress(folded);
    setLastFeedback([]);
  }, [isTeam, module, canAutosave, payload, save.isPending, toast, t]);

  // The cooldown belongs to the shared attempt: a teammate's wrong answer locks my button too.
  useEffect(() => {
    if (!isTeam) return;
    const until = Date.now() + payload.attempt.cooldown_remaining * 1000;
    setCooldownUntil((prev) => Math.max(prev, until));
  }, [isTeam, payload.attempt.submitted_at, payload.attempt.cooldown_remaining]);

  if (!module)
    return (
      <Alert tone="danger">{t("play.unknown_type", { type: payload.puzzle.puzzle_type })}</Alert>
    );
  const Player = module.Player;
  const remaining = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

  const graded = payload.capabilities.graded_feedback;

  const doSubmit = () => {
    const submission = module.serializeSubmission(payload.definition, progressRef.current);
    submit.mutate(submission, {
      onSuccess: (result) => {
        setLastFeedback(result.feedback);
        if (module.applyVerdict) {
          // The type owns its feedback (ADR 0041): the verdict becomes progress, saved at
          // once rather than debounced, so a teammate sees the guess without waiting.
          const next = module.applyVerdict(payload.definition, progressRef.current, {
            correct: result.correct,
            submission,
            feedback: result.feedback,
          });
          progressRef.current = next;
          setProgress(next);
          if (canAutosave) {
            if (saveTimer.current) window.clearTimeout(saveTimer.current);
            persistProgress(next);
          }
        }
        if (result.correct) {
          wasSolvedRef.current = true; // my own solve, not a teammate's
          setCelebrated(true);
          toast(
            t(payload.capabilities.scored ? "play.solved_toast_scored" : "play.solved_toast", {
              time: formatDuration(result.attempt.elapsed_ms ?? 0),
              points: result.score ?? 0,
            }),
            "success",
          );
        } else {
          // A graded guess is information, not a failure: no toast for it (ADR 0041).
          if (!graded) toast(t("play.incorrect_toast"), "danger");
          // A zero cooldown must not lock the button for the tick before `now` catches up.
          if (result.next_cooldown_seconds > 0)
            setCooldownUntil(Date.now() + result.next_cooldown_seconds * 1000);
        }
      },
      onError: (error) => {
        if (error instanceof ApiError && error.status === 429) {
          setCooldownUntil(Date.now() + (error.retryAfter ?? 5) * 1000);
          toast(t("play.cooldown_toast", { seconds: error.retryAfter ?? 5 }), "danger");
        } else if (error instanceof ApiError && error.code === "already_solved") {
          toast(t("play.already_solved"), "info");
        } else {
          toast(t("app.error_generic"), "danger");
        }
      },
    });
  };

  // A Player's Enter key goes through the same gate as the button.
  const requestSubmit = () => {
    if (solved || remaining > 0 || submit.isPending) return;
    doSubmit();
  };

  const rules = extractRules(payload.definition, i18n.language);
  return (
    <section className="bt-play">
      <div className="bt-play__column">
        <Card className="bt-card--sheet">
          <div className="bt-play__header">
            <div>
              <h1 className="bt-play__title">{payload.puzzle.title}</h1>
              <p className="bt-play__author">
                {t("puzzles.by", { author: payload.puzzle.author })}
              </p>
              <PuzzleCreditLine credit={payload.credit} />
            </div>
            <div className="bt-play__status">
              {team ? <Badge tone="info">{t("play.team_badge", { name: team.name })}</Badge> : null}
              {!payload.attempt.is_ranked ? (
                <Badge tone="warning">{t("play.training")}</Badge>
              ) : null}
              <Timer
                ref={timerRef}
                revealedAt={payload.attempt.revealed_at}
                serverNow={serverNow}
                receivedAt={receivedAt}
                frozenMs={solved ? payload.attempt.elapsed_ms : null}
                landed={celebrated}
              />
            </div>
          </div>
          <ParticipantSwitcher
            entry={entry}
            teamId={team?.id ?? null}
            onSelect={onSelectParticipant}
          />
          {team ? (
            <div className="bt-play__notice">
              <Alert tone="info">{t("play.team_notice", { name: team.name })}</Alert>
            </div>
          ) : null}
          {isTeam && !solved && !live ? (
            <p className="bt-play__presence bt-play__presence--offline" role="status">
              {t("play.realtime_offline")}
            </p>
          ) : presenceEnabled ? (
            <p className="bt-play__presence" role="status" aria-live="polite">
              {presence.peers.length > 0
                ? t("play.presence_here", {
                    names: presence.peers.map((peer) => peer.name).join(", "),
                  })
                : t("play.presence_alone")}
            </p>
          ) : null}
          {!payload.attempt.is_ranked ? (
            <div className="bt-play__notice">
              <Alert tone="info">{t("play.training_notice")}</Alert>
            </div>
          ) : null}
          <SolverCount count={solvers} />
          <Suspense fallback={<p className="bt-page-message">{t("app.loading")}</p>}>
            <Player
              definition={payload.definition}
              progress={progress}
              onProgressChange={onProgressChange}
              readOnly={solved}
              liveFeedback={payload.capabilities.live_feedback && tenant.live_feedback_enabled}
              constraintDefinitions={payload.constraint_definitions}
              locale={i18n.language}
              presence={playerPresence}
              onRequestSubmit={requestSubmit}
            />
          </Suspense>
          <div className={cn("bt-play__actions", solved && "bt-play__actions--solved")}>
            {solved ? (
              <SolveCelebration
                elapsedMs={payload.attempt.elapsed_ms ?? 0}
                rank={rank}
                score={score}
                fresh={celebrated}
                originRef={timerRef}
                onReplay={onReplay}
                replaying={replaying}
              />
            ) : (
              <Button
                size="lg"
                onClick={doSubmit}
                loading={submit.isPending}
                disabled={remaining > 0}
              >
                {remaining > 0 ? t("play.submit_in", { seconds: remaining }) : t("play.submit")}
              </Button>
            )}
            <span className="bt-play__attempts">
              {t("play.attempts", { count: payload.attempt.attempts_count })}
            </span>
            {canAutosave && save.isPending ? (
              <span className="bt-play__saving">{t("play.saving")}</span>
            ) : null}
            <SheetLink
              slug={slug}
              puzzleType={payload.puzzle.puzzle_type}
              teamId={team?.id ?? null}
            />
          </div>
          {lastFeedback.length > 0 && !solved && !graded ? (
            <ul className="bt-play__feedback">
              {dedupeFeedback(lastFeedback)
                .slice(0, 6)
                .map(({ code, params }) => (
                  <li key={code}>{t(`feedback.${code}`, { defaultValue: code, ...params })}</li>
                ))}
            </ul>
          ) : null}
        </Card>
        <Card className="bt-card--panel">
          <CardTitle>{t("play.rules")}</CardTitle>
          <RulesText text={rules} />
        </Card>
      </div>
      <div className="bt-play__column">
        {team ? <ChatPanel teamId={team.id} /> : null}
        <LeaderboardPanel slug={slug} />
        <PuzzleStatsPanel slug={slug} />
      </div>
    </section>
  );
}
